import { Injectable } from '@angular/core';
import { Base64 } from 'js-base64';
import { environment } from '@/environments/environment';

/**
 * Segmentos de ruta aceptados. Sólo se permiten rutas definidas en el código
 * fuente (letras, dígitos y - . _ ~), lo que impide inyectar rutas absolutas,
 * cadenas de tipo "//host-externo" o recorridos con "..".
 */
const PATRON_RUTA = /^(?:\/[A-Za-z0-9][A-Za-z0-9._~-]*)+$/;

/**
 * Rutina centralizada de validación de URLs.
 *
 * Toda URL que se use como destino de una petición HTTP o de una redirección
 * debe pasar por este servicio. La validación se hace contra una lista blanca
 * de orígenes aprobados en lugar de confiar en el valor recibido, con lo que se
 * evita que un valor manipulado (configuración, parámetro de ruta, respuesta de
 * un tercero) apunte la aplicación a un sitio externo.
 */
@Injectable({
  providedIn: 'root'
})
export class UrlValidationService {

  /**
   * Orígenes autorizados, tomados de `environment.apiOrigenesPermitidos` y
   * normalizados al arrancar. Se descarta cualquier entrada mal formada, que no
   * sea https o que traiga ruta, credenciales o comodines, de modo que un error
   * de configuración no amplíe el conjunto de destinos permitidos.
   */
  private readonly origenesPermitidos: readonly string[] =
    this.normalizarOrigenes(environment.apiOrigenesPermitidos);

  /**
   * Normaliza una lista blanca declarada en el ambiente. Cada entrada debe ser
   * una URL https absoluta sin ruta; sólo se conserva su origen.
   */
  private normalizarOrigenes(configurados: readonly string[] | undefined): readonly string[] {
    const origenes = configurados ?? [];
    const normalizados: string[] = [];

    for (const origen of origenes) {
      if (typeof origen !== 'string' || origen.length === 0) {
        continue;
      }

      let url: URL;
      try {
        url = new URL(origen);
      } catch {
        continue;
      }

      const sinRuta = url.pathname === '/' || url.pathname === '';
      const esOrigenLimpio = url.username === '' && url.password === '' && url.search === '' && url.hash === '';

      if (url.protocol === 'https:' && sinRuta && esOrigenLimpio && !normalizados.includes(url.origin)) {
        normalizados.push(url.origin);
      }
    }

    return normalizados;
  }

  /**
   * Devuelve la URL base de la API únicamente si el valor configurado en el
   * ambiente resuelve a un origen aprobado.
   *
   * @throws Error si el valor configurado no es una URL https de la lista blanca.
   */
  obtenerBaseApi(): string {
    return this.validarUrl(Base64.decode(environment.apiEndpoint ?? ''));
  }

  /**
   * Construye el endpoint de un servicio validando por separado el origen y la
   * ruta. Se usa en lugar de concatenar cadenas directamente.
   *
   * @param ruta Ruta relativa definida en el código, por ejemplo '/catalogos/CatalogoEquipo'.
   * @throws Error si la ruta contiene caracteres fuera de la lista blanca.
   */
  construirEndpointApi(ruta: string): string {
    if (typeof ruta !== 'string' || !PATRON_RUTA.test(ruta)) {
      throw new Error('Ruta de API no permitida.');
    }
    return this.obtenerBaseApi() + ruta;
  }

  /**
   * Valida una URL absoluta contra la lista blanca de orígenes y la devuelve
   * normalizada (sin credenciales, sin query y sin diagonal final). Comprueba
   * formato, esquema https, ausencia de credenciales embebidas y pertenencia a
   * la lista blanca.
   *
   * @throws Error si la URL es inválida o su origen no está aprobado.
   */
  validarUrl(valorUrl: string): string {
    if (typeof valorUrl !== 'string' || valorUrl.length === 0) {
      throw new Error('URL no permitida: valor vacío.');
    }

    let url: URL;
    try {
      url = new URL(valorUrl);
    } catch {
      throw new Error('URL no permitida: formato inválido.');
    }

    // Sólo https, sin credenciales embebidas (evita "https://api?@atacante.com").
    if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') {
      throw new Error('URL no permitida: esquema o credenciales inválidas.');
    }

    if (this.origenesPermitidos.length === 0) {
      throw new Error('URL no permitida: no hay orígenes autorizados configurados.');
    }

    if (!this.origenesPermitidos.includes(url.origin)) {
      throw new Error('URL no permitida: el destino no está en la lista blanca.');
    }

    // Se reconstruye a partir de valores ya validados, descartando query y hash.
    const ruta = url.pathname.replace(/\/+$/, '');
    return url.origin + ruta;
  }

  /**
   * Variante sin excepciones, útil para decidir si un destino es seguro antes
   * de navegar o redirigir.
   */
  esUrlPermitida(valorUrl: string): boolean {
    try {
      this.validarUrl(valorUrl);
      return true;
    } catch {
      return false;
    }
  }
}
